import React, { ChangeEvent, Fragment, ReactElement, useEffect, useState } from "react";
import {
  IColumn,
  ICasterProduction,
  Order,
  BaseType,
  IProductionExtended,
  IProductionAggregate, IParentSize
} from "../../../../types";
import {
  formatDate,
  formatDateTimeWithSeconds,
  getComparator,
  stableSort
} from "../../../../system/utils";
import {
  Box, Button, Card,
  CircularProgress, Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead, TablePagination,
  TableRow,
  TableSortLabel, Tooltip, Typography
} from "@mui/material";
import StyledTableRow from "../../../../components/styledTableRow";
import IconButton from "@mui/material/IconButton";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Detail from "../../../../components/dialogs/detail";
import CasterHeatInformation from "../casterHeatInformation";
import TableContainer from "../../../../components/tableContainer";
import TablePaginationActions from "../../../../components/tablePaginationActions";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DashboardModuleType, IAcquisitionCell } from "../../../interruptions/types";
import { DateTime } from "luxon";
import ChartDialog from "../../../interruptions/dashboard/dialogs/ChartDialog";
import { useLazyFetch } from "../../../../system/useFetch";
import { ICatalogCategory, ICategoryField } from "../../../config/types";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StartIcon from "@mui/icons-material/Start";

interface IProps {
  productionData: ICasterProduction[];
  parentSize: IParentSize;
}

interface IDetailDialog {
  open: boolean;
  item: ICasterProduction | null;
}

interface IChartInfo {
  from: DateTime;
  to: DateTime;
  cell: IAcquisitionCell;
  heat: IProductionExtended;
  aggregate: IProductionAggregate;
}

interface IDataProcessed {
  status: boolean;
  errors: Record<string, string>;
  fields: ICategoryField[];
}

const columns: IColumn[] = [
  {
    id: "expand",
    label: "",
    minWidth: 80,
  },
  {
    id: "detail",
    label: "",
    minWidth: 80,
  },
  {
    id: "heatNumber",
    label: "Heat Number",
    minWidth: 80,
  },
  {
    id: "sequence",
    label: "Sequence",
    minWidth: 80,
  },
  {
    id: "factoryDay",
    label: "Factory Day",
    minWidth: 80,
    format: value => formatDate(value),
  },
  {
    id: "shiftName",
    label: "Shift",
    minWidth: 80,
  },
  {
    id: "crew",
    label: "Crew",
    minWidth: 80
  },
  {
    id: "steel",
    label: "Steel",
    minWidth: 80,
  },
  {
    id: "beginDatetime",
    label: "Begin Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "endDatetime",
    label: "End Datetime",
    minWidth: 170,
    format: value => formatDateTimeWithSeconds(value),
  },
  {
    id: "ladle",
    label: "Ladle",
    minWidth: 80,
  }
];

interface IRowProps {
  index: number;
  expanded: boolean;
  row: ICasterProduction;
  // setSelectedItem: (item: ICasterProduction | null) => void;
  setOpenDetail: (data: IDetailDialog) => void;
  onShowTrends: (heat: IProductionExtended, aggregate: IProductionAggregate, field: ICategoryField | null) => void;
  catalogs: Record<string, ICatalogCategory[]>;
}

const valueStyle = { fontSize: "1.5em" };

const getFieldData = (aggregate: IProductionAggregate, fields: ICategoryField[]): ICategoryField | null => {

  const getFieldDataInternal = (variable: string, fieldName: string) => {
    if (aggregate.code.includes(variable)) {
      const field = fields.find(x => x.name === fieldName);

      return field ?? null;
    }

    return null;
  };

  return getFieldDataInternal(".MEMS.", "MEMS")
    || getFieldDataInternal(".FEMS.", "FEMS")
    || getFieldDataInternal(".AUTO_SUPER_HEAT", "Superheat")
    || getFieldDataInternal(".SWV_CALCULATION", "Secondary Cooling(SWV)")
    || getFieldDataInternal(".SPEED_ACTUAL", "Casting Speed")
    || null;
};

const getStatusColor = (aggregate: IProductionAggregate, variable: string, fields: ICategoryField[], fieldName: string): string | undefined => {
  if (aggregate.code.includes(variable)) {
    const field = fields.find(x => x.name === fieldName);

    if (field){
      const comparison = field.customOptions && field.customOptions.params && field.customOptions.params.comparison_field
        ? field.customOptions.params.comparison_field.value ?? undefined
        : undefined;

      const key = comparison as keyof IProductionAggregate | undefined;
      if (key && aggregate[key] !== undefined && aggregate[key] !== null) {
        const value = aggregate[key];
        if (typeof value === "number") {
          if (field.maximum && key && value > field.maximum) {
            return `${fieldName} = ${value} > ${field.maximum}`;
          }

          if (field.minimum && key && value < field.minimum) {
            return `${fieldName} = ${value} < ${field.minimum}`;
          }
        }
      } else {
        if (field.maximum && aggregate.max && aggregate.max > field.maximum) {
          return `${fieldName} = ${aggregate.max} > ${field.maximum}`;
        }

        if (field.minimum && aggregate.min && aggregate.min < field.minimum) {
          return `${fieldName} = ${aggregate.min} < ${field.minimum}`;
        }
      }
    }
  }

  return undefined;
};

const RowComponent = ({ row, index, setOpenDetail, onShowTrends, expanded, catalogs }: IRowProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [productionAggregates, setProductionAggregates] = useState<Record<string, IProductionAggregate[]>>({});
  const [ dataProcessed, setDataProcessed ] = useState<IDataProcessed>( { status: true, errors: {}, fields: [] } );

  useEffect(() => {
    setOpen(expanded);
  }, [ expanded ]);

  useEffect(() => {
    const dict: Record<string, IProductionAggregate[]> = {};

    row.productionAggregates.forEach(agg => {
      if (!dict[agg.group]) {
        dict[agg.group] = [agg];
      }  else {
        dict[agg.group].push(agg);
      }
    });

    setProductionAggregates(dict);
  }, [row]);

  useEffect(() => {
    const catalogData = catalogs[row.steel.code];

    if (catalogData) {
      const fields =  catalogData.length ? catalogData[0].content.fields : [];
      const result: IDataProcessed = { status: true, errors: {}, fields };

      Object.values(productionAggregates).forEach(aggregates => {
        aggregates.map((aggregate) => {

          let evalVariable = getStatusColor(aggregate, ".MEMS.", fields, "MEMS");
          if (evalVariable) {
            result.status = false;
            result.errors[aggregate.variable] = evalVariable;
          }

          evalVariable = getStatusColor(aggregate, ".FEMS.", fields, "FEMS");
          if (evalVariable) {
            result.status = false;
            result.errors[aggregate.variable] = evalVariable;
          }

          evalVariable = getStatusColor(aggregate, ".AUTO_SUPER_HEAT", fields, "Superheat");
          if (evalVariable) {
            result.status = false;
            result.errors[aggregate.variable] = evalVariable;
          }

          evalVariable = getStatusColor(aggregate, ".SWV_CALCULATION", fields, "Secondary Cooling(SWV)");
          if (evalVariable && aggregate.min !== 0 && aggregate.max !== 0) {
            result.status = false;
            result.errors[aggregate.variable] = evalVariable;
          }

          evalVariable = getStatusColor(aggregate, ".SPEED_ACTUAL", fields, "Casting Speed");
          if (evalVariable) {
            result.status = false;
            result.errors[aggregate.variable] = evalVariable;
          }
        });
      });

      setDataProcessed(result);
      if (!open && !result.status) {
        setOpen(true);
      }
    } else {
      console.log("NO CATALOG DATA", row);
    }
  }, [catalogs]);

  // onClick={() => setSelectedItem(row)} selected={row === selectedItem}
  return (
    <>
      <StyledTableRow hover={true} role="checkbox" tabIndex={-1}
        style={{ background: !dataProcessed.status ? "red" : undefined }}>
        {columns.map((column: IColumn) => {
          const key = `${row.heatNumber}-${column.id}`;

          if (column.id === "expand") {
            return (
              <TableCell key={key} className="table-cell" >
                <div className="display-flex-row">
                  <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                    {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                  </IconButton>
                </div>
              </TableCell>
            );
          }

          if (column.id === "detail") {
            return (
              <TableCell key={key}  align={column.align}>
                <div className="display-flex-row gap-2 align-items-center">
                  <IconButton size={"small"}
                    onClick={() => setOpenDetail({ open: true, item: row })}>
                    <Tooltip title="Show Detail">
                      <OpenInNewIcon/>
                    </Tooltip>
                  </IconButton>

                  { row.sequence === 1 && <Tooltip title="First of sequence">
                    <StartIcon color="success"/>
                  </Tooltip>}
                </div>
              </TableCell>
            );
          }

          if (column.id === "factoryDay") {
            return (
              <TableCell key={key} align={column.align}>
                {column.format ? column.format(row.shiftHistory.factoryDay) : ""}
              </TableCell>
            );
          }

          if (column.id === "shiftName") {
            return (
              <TableCell key={key}  align={column.align}>
                {row.shiftHistory.name}
              </TableCell>
            );
          }

          if (column.id === "crew") {
            return (
              <TableCell key={key}  align={column.align}>
                {row.crew.name}
              </TableCell>
            );
          }

          if (column.id === "steel") {
            return (
              <TableCell key={key}  align={column.align}>
                {row.steel.code}
              </TableCell>
            );
          }

          if (column.id === "ladle") {
            return (
              <TableCell key={key}  align={column.align}>
                {row.ladleHistory && row.ladleHistory.ladle}
              </TableCell>
            );
          }

          const value = row[column.id as keyof ICasterProduction] as BaseType;

          return (
            <TableCell key={key}  align={column.align}>
              {column.format && typeof value === "number" ? column.format(value) : value}
            </TableCell>
          );
        })}
      </StyledTableRow>
      <StyledTableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: "none" }} colSpan={columns.length}
          className="table-cell">
          <Collapse in={open} timeout="auto" unmountOnExit={true}>
            <Box sx={{ m: 1 }}>
              <Typography variant="h6" gutterBottom={true} component="div">
                Quality Round
              </Typography>

              <div className="display-flex-row">
                <div className="align-self-center " style={{ width: "40px" }}>
                  <div className="menu-option-90" style={{ width: "100px", marginTop: "5px", marginLeft:  "-35px" }}>
                    <strong style={{ fontSize: "2em" }}>{row.heatNumber}</strong>
                  </div>
                </div>

                <div className="display-flex-column">
                  {Object.entries(productionAggregates).map((kv) => {
                    const [ group, aggregates ] = kv;
                    const parentKey = `aggregate-${index}-${row.heatNumber}-${group}`;

                    return (
                      <div key={parentKey} className="display-flex-row ">
                        <div className="align-self-center" style={{ width: "100px" }}>
                          <strong style={{ fontSize: "1.2em" }}>{group}</strong>
                        </div>

                        <div className="display-flex-row justify-content-space-between">
                          {aggregates.map((aggregate, aggregateIndex) => {
                            const color = !dataProcessed.status && dataProcessed.errors[aggregate.variable] !== undefined ? "red" : undefined;

                            return (
                              <Tooltip key={`${parentKey}-${aggregateIndex}`} title={dataProcessed && !dataProcessed.status ? dataProcessed.errors[aggregate.variable] : undefined}>
                                <Card className="display-flex-column p-1 m-1 "
                                  style={{ width: 320, cursor: "pointer", background: color }} onClick={() => onShowTrends(row, aggregate, getFieldData(aggregate, dataProcessed.fields))}>
                                  <div className="align-self-center m-1">
                                    <strong>{aggregate.variable}</strong>
                                  </div>

                                  <div className="display-flex-row justify-content-space-between m-1">
                                    <div className="display-flex-column align-items-center">
                                      <small>Min</small>
                                      <strong style={valueStyle}>{aggregate.min?.toFixed(2)}</strong>
                                    </div>
                                    <div className="display-flex-column align-items-center">
                                      <small>Avg</small>
                                      <strong style={valueStyle}>{aggregate.avg?.toFixed(2)}</strong>
                                    </div>
                                    <div className="display-flex-column align-items-center">
                                      <small>Max</small>
                                      <strong style={valueStyle}>{aggregate.max?.toFixed(2)}</strong>
                                    </div>
                                    <div className="display-flex-column align-items-center">
                                      <small>Std Dev</small>
                                      <strong style={valueStyle}>{aggregate.desStd?.toFixed(2)}</strong>
                                    </div>
                                  </div>
                                </Card>
                              </Tooltip>
                            );
                          })
                          }
                        </div>
                      </div>
                    );
                  })
                  }
                </div>
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </StyledTableRow>
    </>
  );
};

const ProductionTable = ({ productionData, parentSize }: IProps): ReactElement => {
  const [order, setOrder] = useState<Order>("asc");
  const [expanded, setExpanded] = useState<boolean>(false);
  const [orderBy, setOrderBy] = useState<keyof ICasterProduction>("beginDatetime");
  const [openDetail, setOpenDetail] = useState<IDetailDialog>({ open: false, item: null });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [chartInfo, setChartInfo] = useState<IChartInfo | null>(null);
  const [ getCatalogs, { data: catalogs } ] = useLazyFetch<Record<string, ICatalogCategory[]>>();
  const [ specDictionary, setSpecDictionary ] = useState<Record<string, ICatalogCategory[]>>({});

  const dataLength = productionData ? productionData.length : 0;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataLength) : 0;

  useEffect(() => {
    const steelGrades = new Set<string>();

    productionData.forEach(pd => {
      if (!specDictionary[pd.steel.code]) {
        steelGrades.add(pd.steel.code);
      }
    });

    if (steelGrades.size > 0) {
      getCatalogs({
        module: "catalog",
        query: "getCatalogFromProducts",
        payload: {
          areaCode: "MELTSHOP",
          zoneCode: "CASTER",
          catalogName: "Carbon Segregation",
          productNames: [...steelGrades]
        }
      });
    }

  }, [productionData]);

  useEffect(() => {
    if (catalogs) {
      const dict: Record<string, ICatalogCategory[]> = { ...specDictionary };

      Object.entries(catalogs).forEach(kv => {
        const [ key, value ] = kv;
        dict[key] = value;
      });

      setSpecDictionary(dict);
    }
  }, [catalogs]);

  const createSortHandler = (property: keyof ICasterProduction) => () => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number,) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const onShowTrends = (heat: IProductionExtended, aggregate: IProductionAggregate, field: ICategoryField | null) => {
    if (!heat.endDatetime) {
      return;
    }

    // const comparison = field && field.customOptions && field.customOptions.params && field.customOptions.params.comparison_field
    //   ? field.customOptions.params.comparison_field.value ?? undefined
    //   : undefined;
    // const key = comparison as keyof IProductionAggregate | undefined;
    // const value = key && aggregate[key] !== undefined && aggregate[key] !== null ? aggregate[key] : undefined;


    // exclude_seconds_at_start_1seq

    const cell: IAcquisitionCell = {
      key: `${heat.reference}-${aggregate.code}`,
      title: `${heat.reference} - ${aggregate.variable}`,
      module: DashboardModuleType.Acquisition,
      machine: heat.machine.code,
      variables: [{
        hash: "kxj98d97ic3wgf2gpi",
        field: aggregate.code,
        color: aggregate.color ?? "#26c6da",
        unit: aggregate.axis.unit ?? "",
        decimals: 1,
        limits: [],
        annotations: [
          {
            title: "Minimum",
            color: "#E65100",
            value: 0,
            comparison: "MIN"
          },
          {
            title: "Maximum",
            color: "#E65100",
            value: 0,
            comparison: "MAX"
          }
        ],
        yAxis: {
          name: aggregate.axis.name,
          minimum: aggregate.axis.minimum ?? 0,
          maximum: aggregate.axis.maximum ?? 1000,
          ticksCount: 3
        },
        title: aggregate.variable
      }],
      events: [],
      annotations: [
        // {
        //   title: "excluded",
        //   color: "black",
        //   begin: heat.beginDatetime + 18000,
        //   end: heat.beginDatetime + 19000,
        //   type: "Area"
        // }
      ]
    };

    if (field) {
      if (field.minimum) {
        cell.variables[0].limits.push({ title: "Minimum", "color": "#E65100", value: field.minimum });
      }

      if (field.maximum) {
        cell.variables[0].limits.push({ title: "Maximum", "color": "#E65100", value: field.maximum });
      }

      if (field.value && !Number.isNaN(field.value)) {
        cell.variables[0].limits.push({ title: "AIM", "color": "#118a1a", value: field.value as number });
      }
    }

    setChartInfo({
      cell,
      heat,
      aggregate,
      from: DateTime.fromSeconds(heat.beginDatetime),
      to: DateTime.fromSeconds(heat.endDatetime ?? heat.beginDatetime)
    });
  };

  const onExpandAll = () => {
    setExpanded(!expanded);
  };

  const tableContainerHeight = parentSize.height - 48;

  return (
    <>
      <TableContainer className="flex-grow-1" style={{ maxHeight: tableContainerHeight, height: tableContainerHeight }}>
        <Table stickyHeader={true} size={"small"}>
          <TableHead>
            <TableRow>
              {columns.map((column, index) =>
                <TableCell
                  key={`${column.id}-${index}`}
                  align={column.align}
                  style={{ backgroundColor: "#b0bec5", minWidth: column.minWidth }}
                  sortDirection={orderBy === column.id ? order : false}>
                  {column.id !== "actions" && index > 1
                    ? <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={createSortHandler(column.id as keyof ICasterProduction)}
                    >
                      {column.label}
                      {orderBy === column.id
                        ? <span className="cell-visuallyHidden">
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </span>
                        : null}
                    </TableSortLabel>
                    : <Fragment/>}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {productionData && stableSort<ICasterProduction>(productionData, getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: ICasterProduction, index: number) => {
                // selectedItem={selectedItem} setSelectedItem={setSelectedItem}

                return (
                  <RowComponent key={`prod-eaf-row-${index}`} row={row} index={index} expanded={expanded}
                    catalogs={specDictionary ?? {}} setOpenDetail={setOpenDetail} onShowTrends={onShowTrends}/>
                );
              })}
            {emptyRows > 0 &&
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6}/>
              </TableRow>
            }
          </TableBody>
        </Table>

        {!productionData &&
          <Paper className="display-flex-row justify-content-center align-items-center w-100 p-2">
            <CircularProgress className=""/>
          </Paper>
        }
        {openDetail.open && openDetail.item &&
          <Detail closed={() => setOpenDetail({ open: false, item: null })} fullWidth={true} maxWidth={"xl"}
            title={`Detail - ${openDetail.item.machine.name} - ${openDetail.item.heatNumber}`}>
            <CasterHeatInformation productionItem={openDetail.item} height={500}/>
          </Detail>
        }
        {chartInfo && <ChartDialog onCancel={() => setChartInfo(null)} cell={chartInfo.cell} from={chartInfo.from} to={chartInfo.to}/>}
      </TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell className="p-0">
              <Button onClick={onExpandAll} variant="contained" color="info" startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon/> }>
                {expanded ? "Collapse All" : "Expand All"}
              </Button>
            </TableCell>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, { label: "All", value: -1 }]}
              colSpan={columns.length + 1}
              count={dataLength}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default ProductionTable;
